import {useMutation, useQuery} from '@apollo/client';
import Button from '@material-ui/core/Button';
import TableCell from '@material-ui/core/TableCell/TableCell';
import TablePaginaation from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow/TableRow';
import Skeleton from '@material-ui/lab/Skeleton';
import {Admin} from 'packages/common-types/schema-types';
import {GET_ADMINS} from 'packages/graphql/v2/queries';
import {DELETE_ADMIN} from 'packages/graphql/v2/mutations';
import {ADMIN_HEADERS} from 'packages/constants/table-constants';
import ConfirmAction from 'packages/dialogs/ConfirmAction';
import {useModal} from 'packages/hooks/use-modal';
import useTablePagination from 'packages/hooks/use-table-pagination';
import useTableSearch from 'packages/hooks/use-table-search';
import BaseTable from 'packages/tables/BaseTable';
import {useState, useRef} from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import useStyles from '../../tables/tables-styles';

type GetAdminsData = {
  getAdmins: Admin[];
};

type DeleteAdminVars = {
  id: string;
};

const Admins = () => {
  const {
    data,
    loading: fetching,
    refetch,
  } = useQuery<GetAdminsData>(GET_ADMINS);
  const {
    page,
    rowsPerPage,
    onChangePage: onChangePagination,
    onChangeRowsPerPage,
  } = useTablePagination();

  const [loading, setLoading] = useState(false);
  const {toggle, modalOpen} = useModal();
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const {filteredRows, onChange} = useTableSearch(data?.getAdmins);
  const classes = useStyles(); // this variable is to put margin-top for the select bar.
  const [deleteAdmin] = useMutation<boolean, DeleteAdminVars>(DELETE_ADMIN);
  const selectRef = useRef(null);

  if (!data || fetching) {
    return (
      <>
        <div style={{marginTop: 30}}>
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
        </div>
      </>
    );
  }

  const handleDeleteAdmin = async () => {
    try {
      setLoading(true);
      await deleteAdmin({
        variables: {
          id: selectedAdmin,
        },
      });
      refetch();
      toggle();
      selectRef.current.select.clearValue();
      Swal.fire({
        title: 'Eliminado',
        text: 'El administrador ha sido eliminado',
        icon: 'success',
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo eliminar el administrador',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * @returns this function returns the table with the information that users filter.
   */
  const renderTableData = () => {
    return filteredRows
      ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((admin) => {
        const {fullName, uid, phoneNumber, email} = admin;
        return (
          <TableRow key={uid}>
            <TableCell component="th" scope="row">
              {fullName}
            </TableCell>
            <TableCell align="right">{phoneNumber}</TableCell>
            <TableCell align="right">{email}</TableCell>
            <TableCell align="right">
              <Button
                onClick={() => {
                  setSelectedAdmin(uid);
                  toggle();
                }}
                size="small"
                color="secondary"
                variant="text"
              >
                Eliminar
              </Button>
            </TableCell>
          </TableRow>
        );
      });
  };

  return (
    <>
      <ConfirmAction
        toggle={toggle}
        title="Eliminar administrador"
        description="¿Estás seguro de querer eliminar este administrador?"
        modalOpen={modalOpen}
        loading={loading}
        onConfirm={handleDeleteAdmin}
      />
      <Select
        ref={selectRef}
        className={classes.table_container}
        options={filteredRows}
        isClearable
        isLoading={fetching}
        getOptionLabel={({fullName}) => fullName}
        getOptionValue={({uid}) => uid}
        placeholder="Buscar un administrador"
        onChange={onChange}
      />
      <BaseTable headers={ADMIN_HEADERS}>{renderTableData()}</BaseTable>
      <TablePaginaation
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={data?.getAdmins.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePagination}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};

export default Admins;
